import { faCopyright } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, JoonUIColor, Typography } from "@joonapp/web-shared"
import { useEffect } from "react"
import toast from "react-hot-toast"
import { Trans, useTranslation } from "react-i18next"
import { create } from "zustand"

import meTabPreview from "/images/previews/referral_preview_card_me_tab.png"
import questsPreview from "/images/previews/referral_preview_card_quests.png"
import rewardsPreview from "/images/previews/referral_preview_card_rewards.png"

import ReferralPreviewCard from "./ReferralPreviewCard"
import ReferralPreviewCarousel from "./ReferralPreviewCarousel"
import ReferralTestimonialCard from "./ReferralTestimonialCard"
import ReferralTestimonialCarousel from "./ReferralTestimonialCarousel"
import FiveStars from "../../components/fiveStars/FiveStars"
import { parentReferralTestimonials } from "../../constants"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver"
import useMediaQuery from "../../hooks/useMediaQuery"
import useMobile from "../../hooks/useMobile"
import { useUserQuery } from "../../networking/queries"
import { useReferralInfoQuery } from "../../networking/users/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"

interface ReferralLandingPageStore {
  uuid: string | null
  setUuid: (uuid: string) => void
}

export const useReferralLandingPageStore = create<ReferralLandingPageStore>(
  (set) => ({
    uuid: "",
    setUuid: (uuid: string) => set({ uuid }),
  })
)

const ReferralLandingPage = () => {
  useDocumentTitle("Joon - Referral")
  const { setUuid } = useReferralLandingPageStore()
  const { user } = useUserQuery()
  const {
    data: referralInfo,
    isLoading: isLoadingReferralInfo,
    isError: isInvalidReferral,
  } = useReferralInfoQuery()
  const router = useRouter()
  const isMobile = useMobile()
  const shouldShowPreviewCarousel = useMediaQuery("(max-width: 1120px)")
  const { t } = useTranslation()

  const { uuid } = router.query

  // Intersection observers for tracking section views
  const {
    elementRef: referralSectionRef,
    isIntersecting: isReferralSectionIntersecting,
  } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    triggerOnce: true,
  })

  const {
    elementRef: testimonialsSectionRef,
    isIntersecting: isTestimonialsSectionIntersecting,
  } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    triggerOnce: true,
  })

  const referralPreviewCards = [
    {
      text: t("REFERRAL_PREVIEW_1"),
      image: meTabPreview,
    },
    {
      text: t("REFERRAL_PREVIEW_2"),
      image: questsPreview,
    },
    {
      text: t("REFERRAL_PREVIEW_3"),
      image: rewardsPreview,
    },
  ]

  useEffect(() => {
    if (uuid && typeof uuid === "string") {
      setUuid(uuid)
      router.replace("/referral-landing")
      trackAnalyticEvent(ANALYTIC_EVENTS.REFERRAL_LANDING_PAGE_VIEWED, {
        uuid,
      })
    }
  }, [uuid, setUuid, router])

  const onActivateGuestPass = (placement: "top" | "bottom") => {
    if (!referralInfo) return
    trackAnalyticEvent(ANALYTIC_EVENTS.ACTIVATE_GUEST_PASS, {
      uuid: referralInfo.uuid,
      placement,
    })
    localStorage.setItem(
      localStorageItems.joonGiftCardReferralCode,
      referralInfo.uuid
    )
    router.push("/onboard")
    toast.success(t("REFERRAL_LANDING_TOAST"))
  }

  useEffect(() => {
    if (!!user) {
      router.push("/me")
    }
  }, [user, router])

  // Track analytics when sections come into view
  useEffect(() => {
    if (isReferralSectionIntersecting) {
      trackAnalyticEvent(ANALYTIC_EVENTS.REFERRAL_FEATURE_CAROUSEL_VIEWED, {
        isMobile: isMobile,
      })
    }
    if (isTestimonialsSectionIntersecting) {
      trackAnalyticEvent(ANALYTIC_EVENTS.REFERRAL_TESTIMONIALS_VIEWED, {
        isMobile: isMobile,
      })
    }
  }, [
    isReferralSectionIntersecting,
    isTestimonialsSectionIntersecting,
    isMobile,
  ])

  useEffect(() => {
    if (isInvalidReferral) {
      trackAnalyticEvent(ANALYTIC_EVENTS.REFERRAL_INVALID_LINK)
      alert(t("REFERRAL_LANDING_INVALID"))
    }
  }, [isInvalidReferral, t])

  return (
    <div className="w-full flex flex-col items-center justify-start min-h-screen">
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex flex-col items-center relative bg-gradient-to-b from-white to-blue-50">
          <div
            className="flex flex-col items-center gap-6 text-center p-6 pb-16 max-xs:gap-4"
            style={{ width: "min(100%, 520px)" }}
          >
            <img
              src="/images/logos/logo_accent.png"
              alt="Joon"
              className="h-11 max-xs:h-8 pointer-events-none mt-4"
            />

            <Typography variant="h1">
              <div className="max-sm:max-w-[300px] max-xs:text-2xl">
                {t("REFERRAL_LANDING_CLAIM")}
              </div>
            </Typography>

            <Typography variant="body">
              {t("REFERRAL_LANDING_SHARED_BY", {
                name:
                  referralInfo?.inviter_name ?? t("REFERRAL_LANDING_A_FRIEND"),
              })}
            </Typography>

            <img
              src="/images/referral_gift_card.png"
              alt="Joon 30-Day Guest Pass"
              style={{
                maxWidth: "300px",
                width: isMobile ? "auto" : "100%",
                maxHeight: isMobile ? window.innerHeight * 0.3 : "300px",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />

            <Typography variant="bodySmall">
              {t("REFERRAL_LANDING_DISCLAIMER")}
            </Typography>

            <Button
              text={
                isInvalidReferral
                  ? t("REFERRAL_LANDING_ASK_NEW_LINK")
                  : t("REFERRAL_LANDING_ACTIVATE")
              }
              onClick={() => onActivateGuestPass("top")}
              fullWidth
              disabled={!referralInfo}
              style={{ width: "min(100%, 300px)" }}
              isLoading={isLoadingReferralInfo}
            />
          </div>
          <svg
            className="absolute left-0 bottom-0 w-full h-16"
            viewBox="0 0 1200 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <path
              d="M 0 20 Q 120 10 240 20 T 480 20 T 720 20 T 960 20 T 1200 20 L 1200 40 L 0 40 Z"
              fill="#fff"
            />
          </svg>
        </div>
        <div
          className="flex flex-col items-center gap-6 text-center p-6 pt-0"
          style={{ width: "min(100%, 400px)" }}
        >
          <div className="flex flex-col items-center gap-3">
            <Typography variant="h2Serif">
              {t("REFERRAL_LANDING_WHY")}
            </Typography>
            <div className="flex flex-col items-center">
              <Typography variant="h4">
                {t("REFERRAL_LANDING_REGAIN")}
              </Typography>
              <Typography variant="bodySmall">
                <Trans
                  i18nKey="REFERRAL_LANDING_BYE_CHAOS"
                  components={{
                    i: (
                      <Typography variant="bodySmall" className="font-italic">
                        {""}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </div>
          </div>
        </div>

        <div
          ref={referralSectionRef}
          className="w-full flex flex-row items-center justify-center gap-4 px-4 mb-16"
          style={{ maxWidth: "100vw" }}
        >
          {shouldShowPreviewCarousel ? (
            <ReferralPreviewCarousel items={referralPreviewCards} />
          ) : (
            referralPreviewCards.map((card) => (
              <ReferralPreviewCard key={card.text} preview={card} />
            ))
          )}
        </div>
        <div
          className="flex flex-col items-center gap-6 text-center p-6 pt-0"
          style={{ width: "min(100%, 400px)" }}
        >
          <div className="flex flex-col items-center gap-3 w-full">
            <Typography variant="h2Serif">
              {t("REFERRAL_LANDING_TRUSTED")}
            </Typography>
            <div className="flex flex-row items-center gap-2">
              <FiveStars />
              <Typography variant="body">
                <Trans
                  i18nKey="REFERRAL_LANDING_RATING"
                  components={{
                    b: <Typography variant="bodyBold">{""}</Typography>,
                  }}
                />
              </Typography>
            </div>
          </div>
        </div>
        <div
          ref={testimonialsSectionRef}
          className="flex flex-col items-center gap-3 w-full px-4"
        >
          {isMobile ? (
            <ReferralTestimonialCarousel items={parentReferralTestimonials} />
          ) : (
            <div className="w-full max-w-[1000px] flex flex-row items-center gap-2 flex-wrap justify-center">
              {parentReferralTestimonials.map((testimonial) => (
                <ReferralTestimonialCard testimonial={testimonial} />
              ))}
            </div>
          )}
        </div>
        <Button
          text={
            isInvalidReferral
              ? t("REFERRAL_LANDING_ASK_NEW_LINK")
              : t("REFERRAL_LANDING_ACTIVATE")
          }
          onClick={() => onActivateGuestPass("bottom")}
          fullWidth
          disabled={!referralInfo}
          style={{ width: "min(100%, 300px)" }}
          className="my-12"
          isLoading={isLoadingReferralInfo}
        />
        <div className="flex flex-col items-center justify-center w-full h-[126px] bg-indigo-500 mbm">
          <img
            src="/images/logos/logo_white.svg"
            alt="Joon"
            className="w-auto h-6"
            style={{ pointerEvents: "none" }}
          />
          <Typography variant="bodySmall" color={JoonUIColor.text.inverted}>
            <Trans
              i18nKey="REFERRAL_LANDING_COPYRIGHT"
              components={{
                icon: (
                  <FontAwesomeIcon
                    icon={faCopyright}
                    fontSize={12}
                    color={JoonUIColor.text.inverted}
                  />
                ),
              }}
            />
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default ReferralLandingPage
